import { useMemo, useRef, useState } from 'react';

import { OpsRotaApiError } from '@/services/opsRota/client';
import type { OpsSchedule } from '@/services/opsRota/types';
import { trackOpsScheduleChanged, trackOpsScheduleFailed } from '@/util/vexo';

import { failReason, isOvernight } from '../analytics';
import { rangeDates } from '../dates';
import {
  useOpsManagers,
  useOpsSchedules,
  useScheduleMutations,
} from '../hooks/useOpsRota';
import {
  nothingRepeatedText,
  REPEAT_SOURCE_GONE,
  REPEAT_TITLE,
  repeatedToast,
  SAVE_FAILED,
} from '../messages';
import type { ToastCopy } from '../messages';
import { gridRange, pickRange, Selection } from '../monthGrid';
import {
  DialogContent,
  DialogFooter,
  PrimaryButton,
  TextButton,
} from '../Operations.styles';
import {
  canSave,
  fromSchedule,
  isInactiveManager,
  toTimes,
} from '../scheduleForm';

import ErrorList from './ErrorList';
import MonthCalendar from './MonthCalendar';
import OpsDialog from './OpsDialog';
import ScheduleFields from './ScheduleFields';

type Props = {
  /** The block being repeated (its date may be in the past). */
  schedule: OpsSchedule;
  /** Ops-zone today: earlier dates cannot be picked. */
  today: string;
  /** The month the Operations calendar shows; the modal opens on it (§11). */
  month: string;
  /** Focus goes back here on close (the block's kebab). */
  returnFocus?: HTMLElement | null;
  onClose: () => void;
  /** Saved: the page shows the toast and closes the modal. */
  onDone: (toast: ToastCopy) => void;
};

/**
 * "Pick a date to repeat schedule" (UI spec §11). Its own calendar
 * (the §7.1 range rule, today and later only) and the block's times and
 * managers, pre-filled and editable. Every date in the picked range gets a
 * copy; graphy skips a manager who would overlap another block there.
 */
const RepeatScheduleModal = ({
  schedule,
  today,
  month: initialMonth,
  returnFocus,
  onClose,
  onDone,
}: Props) => {
  const [month, setMonth] = useState(initialMonth);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [values, setValues] = useState(() => fromSchedule(schedule));
  const [error, setError] = useState<string[] | null>(null);
  const inFlight = useRef(false);

  // Same query key as the page's month grid, so the dots come from cache.
  const monthQuery = useOpsSchedules(gridRange(month), { keepPrevious: true });
  const managersQuery = useOpsManagers();
  const { repeat } = useScheduleMutations();

  const monthSchedules = useMemo(
    () => monthQuery.data?.schedules ?? [],
    [monthQuery.data],
  );
  const marked = useMemo(
    () => new Set(monthSchedules.map((s) => s.date)),
    [monthSchedules],
  );
  const flagged = useMemo(
    () =>
      new Set(
        monthSchedules
          .filter((s) => s.date >= today && s.managers.some((m) => !m.active))
          .map((s) => s.date),
      ),
    [monthSchedules, today],
  );

  const roster = managersQuery.data;
  // BR-06: a deactivated manager on the source block must be removed first.
  const hasInactive = values.managerIds.some((id) =>
    isInactiveManager(id, roster, schedule.managers),
  );
  const busy = repeat.isPending;
  const ready = selection !== null && canSave(values) && !hasInactive && !busy;

  const pick = (date: string) => {
    setSelection((current) => pickRange(current, date, today));
    setError(null);
  };

  const save = async () => {
    if (!ready || !selection || inFlight.current) return;
    inFlight.current = true;
    setError(null);
    const dates = rangeDates(selection.start, selection.end);
    try {
      const result = await repeat.mutateAsync({
        id: schedule.id,
        input: { dates, ...toTimes(values) },
      });
      // Every date skipped: stay open and say why, rather than toasting a
      // save that wrote nothing.
      if (result.schedules.length === 0) {
        trackOpsScheduleFailed({ action: 'repeat', reason: 'nothing_written' });
        setError([nothingRepeatedText(result.skipped)]);
        return;
      }
      const written = new Set(
        result.schedules.flatMap((s) => s.managers.map((m) => m.id)),
      ).size;
      trackOpsScheduleChanged({
        action: 'repeat',
        days_count: result.schedules.length,
        managers_count: written,
        overnight: isOvernight(result.schedules[0]),
        skipped_count: result.skipped.length,
      });
      // The range is the dates actually written, not the span picked
      // (a first or last date where every manager was skipped is left out).
      onDone(
        repeatedToast(
          // The managers actually written; a manager skipped on
          // every date was not scheduled anywhere.
          written,
          result.schedules.map((s) => s.date),
          result.skipped,
        ),
      );
    } catch (err) {
      trackOpsScheduleFailed({ action: 'repeat', ...failReason(err, true) });
      if (!(err instanceof OpsRotaApiError)) setError([SAVE_FAILED]);
      // 404: the source block was deleted meanwhile.
      else if (err.status === 404) setError([REPEAT_SOURCE_GONE]);
      else setError(err.messages);
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <OpsDialog
      title={REPEAT_TITLE}
      testId="opsRepeatModal"
      closeTestId="opsRepeatCloseButton"
      closeDisabled={busy}
      returnFocus={returnFocus}
      onClose={onClose}
    >
      <DialogContent aria-busy={busy}>
        {/* A block wrapper: the calendar's flex sizing is for the page row. */}
        <div>
          <MonthCalendar
            month={month}
            onMonthChange={setMonth}
            today={today}
            selection={selection}
            marked={marked}
            flagged={flagged}
            onSelect={pick}
            isDisabled={(date) => date < today}
            dimmed={busy}
            lightSingle
            testIdPrefix="opsRepeatCalendar"
            dayTestIdPrefix="opsRepeatDay"
          />
        </div>
        <ScheduleFields
          idPrefix="opsRepeat"
          values={values}
          onChange={setValues}
          roster={roster}
          rosterError={managersQuery.isError}
          onRetryRoster={() => managersQuery.refetch()}
          assigned={schedule.managers}
          disabled={busy}
          layer="modal"
        />
        {error && (
          <ErrorList messages={error} testId="opsRepeatError" focusOnShow />
        )}
      </DialogContent>
      <DialogFooter>
        <TextButton
          type="button"
          disabled={busy}
          data-testid="opsRepeatCancelButton"
          onClick={onClose}
        >
          Cancel
        </TextButton>
        <PrimaryButton
          type="button"
          disabled={!ready}
          data-testid="opsRepeatSaveButton"
          css={{ width: '120px' }}
          onClick={save}
        >
          {busy ? 'Saving…' : 'Save'}
        </PrimaryButton>
      </DialogFooter>
    </OpsDialog>
  );
};

export default RepeatScheduleModal;
